import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Eye, EyeOff, Edit, X, RefreshCw, Trash2 } from "lucide-react";
import { Button } from "./ui/button";
import { toast, Toaster } from "react-hot-toast";
import API_BASE_URL, {
  getAllProducts,
  updatePrintfulProduct,
  syncPrintfulProducts,
  syncStripeProducts,
  getOrders,
  getPrintfulOrder,
  confirmPrintfulOrder,
  deleteProduct,
  deleteProductGroup,
} from "../services/api";
import LoginForm from "./auth/LoginForm";
import { useAuth } from "../context/AuthContext";

const PLACEHOLDER_IMAGE =
  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiNFNUU3RUIiLz48cGF0aCBkPSJNODAgOTBINzBWMTEwSDgwVjkwWiIgZmlsbD0iIzZCNzI4MCIvPjxwYXRoIGQ9Ik0xMzAgOTBIMTIwVjExMEgxMzBWOTBaIiBmaWxsPSIjNkI3MjgwIi8+PHBhdGggZD0iTTEwMCAxMzBDMTEzLjgwNyAxMzAgMTI1IDExOC44MDcgMTI1IDEwNUMxMjUgOTEuMTkyNSAxMTMuODA3IDgwIDEwMCA4MEM4Ni4xOTI5IDgwIDc1IDkxLjE5MjkgNzUgMTA1Qzc1IDExOC44MDcgODYuMTkyOSAxMzAgMTAwIDEzMFoiIGZpbGw9IiM2QjcyODAiLz48L3N2ZyI+";

const ImageWithFallback = ({ src, alt, className }) => {
  const [imgSrc, setImgSrc] = useState(src);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setImgSrc(src);
    setHasError(false);
  }, [src]);

  const handleError = () => {
    if (!hasError) {
      setHasError(true);
      setImgSrc(PLACEHOLDER_IMAGE);
    }
  };

  return (
    <img src={imgSrc} alt={alt} className={className} onError={handleError} />
  );
};

ImageWithFallback.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  className: PropTypes.string,
};

const validateUrl = (path, strict = false) => {
  if (!path) return true;
  if (!strict) {
    return (
      !path.trim() ||
      path.startsWith("/") ||
      path.startsWith("http://") ||
      path.startsWith("https://")
    );
  }
  if (path.startsWith("/")) return true;
  try {
    new URL(path);
    return true;
  } catch (error) {
    return false;
  }
};

const ImageManager = ({ product, onUpdate }) => {
  const [images, setImages] = useState(product.additionalImages || []);
  const [newImageUrl, setNewImageUrl] = useState("");
  const [error, setError] = useState("");

  const handleAddImage = () => {
    if (!validateUrl(newImageUrl)) {
      setError(
        "Image path must start with / for relative paths, or http:// or https:// for URLs",
      );
      return;
    }
    setImages([...images, newImageUrl]);
    setNewImageUrl("");
    setError("");
    onUpdate([...images, newImageUrl]);
  };

  const handleRemoveImage = (urlToRemove) => {
    const updatedImages = images.filter((url) => url !== urlToRemove);
    setImages(updatedImages);
    onUpdate(updatedImages);
  };

  return (
    <div>
      <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
        Additional Images
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        {images.map((url) => (
          <div
            key={url}
            className="relative group aspect-square bg-gray-50 dark:bg-gray-700 rounded-lg overflow-hidden"
          >
            <ImageWithFallback
              src={url}
              alt="Additional product view"
              className="w-full h-full object-cover"
            />
            <button
              onClick={() => handleRemoveImage(url)}
              className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label="Remove image"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
      <div>
        <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
          Add Image Path/URL
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={newImageUrl}
            onChange={(e) => {
              setNewImageUrl(e.target.value);
              setError("");
            }}
            placeholder="/images/products/example.jpg"
            className="flex-1 p-2 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <Button onClick={handleAddImage} disabled={!newImageUrl}>
            Add Image
          </Button>
        </div>
        {error && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>
        )}
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Enter a relative path (e.g., /images/products/example.jpg) or full URL
        </p>
      </div>
    </div>
  );
};

ImageManager.propTypes = {
  product: PropTypes.shape({
    additionalImages: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  onUpdate: PropTypes.func.isRequired,
};

const VariantManager = ({ product, onUpdate, onToggleVisibility }) => {
  const [variants, setVariants] = useState(product.variants || []);

  useEffect(() => {
    setVariants(product.variants || []);
  }, [product]);

  const handleToggleVariantVisibility = (variant) => {
    onToggleVisibility(variant);
  };

  return (
    <div className="mt-4 p-4 border-t dark:border-gray-700">
      <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
        Product Variants
      </h3>
      <div className="space-y-2">
        {variants.map((variant, index) => {
          // Create display name with both color and size if available
          const displayParts = [];
          if (variant.color) displayParts.push(variant.color);
          if (variant.size) displayParts.push(variant.size);
          const displayName = displayParts.length > 0 ? displayParts.join(', ') : 'Variant';
          
          return (
            <div
              key={`${variant._id}-${index}-${variant.color || ''}-${variant.size || ''}`}
              className="flex items-center justify-between p-2 border dark:border-gray-700 rounded"
            >
              <div className="flex-1">
                <p className="font-medium text-gray-900 dark:text-white">
                  {displayName}
                </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                ${parseFloat(variant.price).toFixed(2)} -{" "}
                {variant.inStock ? "In Stock" : "Out of Stock"}
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleToggleVariantVisibility(variant)}
              >
                {variant.isVisible ? (
                  <Eye className="h-4 w-4" />
                ) : (
                  <EyeOff className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
};

VariantManager.propTypes = {
  product: PropTypes.shape({
    variants: PropTypes.arrayOf(
      PropTypes.shape({
        size: PropTypes.string.isRequired,
        price: PropTypes.number.isRequired,
        inStock: PropTypes.bool.isRequired,
        isVisible: PropTypes.bool.isRequired,
        _id: PropTypes.string.isRequired,
      }),
    ),
  }).isRequired,
  onUpdate: PropTypes.func.isRequired,
  onToggleVisibility: PropTypes.func.isRequired,
};

const MockupDisplay = ({ files }) => {
  if (!files || files.length === 0) {
    return (
      <div className="p-4 text-gray-500 dark:text-gray-400">
        No mockups available
      </div>
    );
  }

  const previewFiles = files.filter(
    (file) => file.type === "preview" && !file.is_temporary,
  );

  if (previewFiles.length === 0) {
    return (
      <div className="p-4 text-gray-500 dark:text-gray-400">
        No preview mockups available
      </div>
    );
  }

  return (
    <div className="p-4">
      <h3 className="font-bold text-lg mb-4 text-gray-900 dark:text-white">
        Printful Mockups
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {previewFiles.map((file) => (
          <div
            key={`${file.id}-${file.filename}`}
            className="border dark:border-gray-700 rounded-lg overflow-hidden"
          >
            <ImageWithFallback
              src={file.preview_url || file.url}
              alt={file.filename}
              className="w-full h-auto"
            />
            <div className="p-2 text-xs text-gray-500 dark:text-gray-400 truncate">
              {file.filename}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

MockupDisplay.propTypes = {
  files: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      type: PropTypes.string,
      url: PropTypes.string,
      preview_url: PropTypes.string,
      filename: PropTypes.string,
      is_temporary: PropTypes.bool,
    }),
  ),
};

const ProductEditor = ({ product, onUpdate, onToggleVariantVisibility, onToggleGroupVisibility, onDelete }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedProduct, setEditedProduct] = useState(product);
  const [error, setError] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [isPrintfulProduct, setIsPrintfulProduct] = useState(false);

  useEffect(() => {
    setIsPrintfulProduct(!!product.sync_product_id);
    setEditedProduct(product);
  }, [product]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!validateUrl(editedProduct.image, true)) {
      setError(
        "Please enter a valid image URL (must start with http:// or https://)",
      );
      return;
    }

    try {
      const productToUpdate = {
        ...editedProduct,
        _id: product._id,
        id: product.id,
        external_id: product.external_id,
        sync_product_id: product.sync_product_id,
      };

      if (isPrintfulProduct) {
        await onUpdate(productToUpdate, true);
      } else {
        await onUpdate(productToUpdate, false);
      }
      setIsEditing(false);
    } catch (error) {
      setError(error.message);
    }
  };

  const handleImageChange = (e) => {
    const path = e.target.value;
    setEditedProduct({ ...editedProduct, image: path });

    if (path && !validateUrl(path)) {
      setError(
        "Image path must start with / for relative paths, or http:// or https:// for URLs",
      );
    } else {
      setError("");
    }
  };

  const productImage =
    product.image ||
    product.product?.image ||
    product.files?.find((f) => f.type === "preview" && !f.is_temporary)
      ?.preview_url ||
    product.parent_product?.thumbnail_url;

  return (
    <div className="p-4 border dark:border-gray-700 rounded-lg mb-4">
      <div
        className="flex justify-between items-start cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded overflow-hidden flex-shrink-0">
            <ImageWithFallback
              src={productImage}
              alt={product.name}
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <h3 className="font-bold text-xl text-gray-900 dark:text-white">
              {product.name}
            </h3>
            <div className="flex items-center">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Price:
              </span>
              <span className="ml-2 text-sm font-medium text-gray-900 dark:text-white">
                $
                {isPrintfulProduct
                  ? parseFloat(product.retail_price || 0).toFixed(2)
                  : parseFloat(product.price || 0).toFixed(2)}
              </span>
              ,
            </div>
            <div className="flex items-center">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Category:
              </span>
              <span className="ml-2 text-sm font-medium text-gray-900 dark:text-white">
                {product.category || "Uncategorized"}
              </span>
              ,
            </div>
            {isPrintfulProduct && (
              <div className="inline-block text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded mt-1">
                Printful Product
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              setIsEditing(!isEditing);
              setError("");
              setEditedProduct(product);
              if (!isExpanded) setIsExpanded(true);
            }}
          >
            {isEditing ? (
              <X className="h-4 w-4" />
            ) : (
              <Edit className="h-4 w-4" />
            )}
          </Button>
          {/* Group visibility toggle */}
          <Button
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              if (typeof onToggleGroupVisibility === 'function') {
                onToggleGroupVisibility(product);
              }
            }}
          >
            {product.variants.every((v) => v.isVisible !== false) ? (
              <Eye className="h-4 w-4" />
            ) : (
              <EyeOff className="h-4 w-4" />
            )}
          </Button>
          {/* Delete button */}
          <Button
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              if (typeof onDelete === 'function') {
                onDelete(product);
              }
            }}
            className="text-red-600 hover:text-red-800 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
          >
            <svg
              className={`w-4 h-4 transition-transform ${isExpanded ? "rotate-180" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </Button>
        </div>
      </div>

      {isExpanded && (
        <div className="mt-4">
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded text-red-600 dark:text-red-400">
              {error}
            </div>
          )}

          {isEditing ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                  Name
                </label>
                <input
                  type="text"
                  value={editedProduct.name}
                  onChange={(e) =>
                    setEditedProduct({ ...editedProduct, name: e.target.value })
                  }
                  className="w-full p-2 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                  Description
                </label>
                <textarea
                  value={editedProduct.description || ""}
                  onChange={(e) =>
                    setEditedProduct({
                      ...editedProduct,
                      description: e.target.value,
                    })
                  }
                  className="w-full p-2 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  required
                />
              </div>
              {!isPrintfulProduct && (
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                    Base Price
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={editedProduct.price}
                    onChange={(e) =>
                      setEditedProduct({
                        ...editedProduct,
                        price: parseFloat(e.target.value),
                      })
                    }
                    className="w-full p-2 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    min="0"
                  />
                </div>
              )}
              {!isPrintfulProduct && (
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                    Main Image Path/URL
                  </label>
                  <input
                    type="text"
                    value={editedProduct.image}
                    onChange={handleImageChange}
                    className="w-full p-2 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    placeholder="/images/products/example.jpg"
                  />
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Enter a relative path (e.g., /images/products/example.jpg)
                    or full URL
                  </p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                  Category
                </label>
                <input
                  type="text"
                  value={editedProduct.category || ""}
                  onChange={(e) =>
                    setEditedProduct({
                      ...editedProduct,
                      category: e.target.value,
                    })
                  }
                  className="w-full p-2 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter category"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                  Character
                </label>
                <input
                  type="text"
                  value={editedProduct.character || ""}
                  onChange={(e) =>
                    setEditedProduct({
                      ...editedProduct,
                      character: e.target.value,
                    })
                  }
                  className="w-full p-2 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter character"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsEditing(false);
                    setError("");
                    setEditedProduct(product);
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={!!error}>
                  Save Changes
                </Button>
              </div>
            </form>
          ) : (
            <div className="space-y-2">
              <p className="text-gray-900 dark:text-gray-300">
                {product.description || "No description available."}
              </p>
              {isPrintfulProduct && (
                <>
                  {product.sku && (
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      <span className="font-medium">SKU:</span> {product.sku}
                    </p>
                  )}
                  {product.size && (
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      <span className="font-medium">Size:</span> {product.size}
                    </p>
                  )}
                  {product.color && (
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      <span className="font-medium">Color:</span>{" "}
                      {product.color}
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          <div className="mt-4 space-y-4">
            <VariantManager
              product={product}
              onUpdate={(variants) =>
                onUpdate({ ...product, variants }, true)
              }
              onToggleVisibility={onToggleVariantVisibility}
            />
            {product.variants && product.variants.length > 0 && (
              <VariantMockupDisplay variants={product.variants} />
            )}
            <ImageManager
              product={product}
              onUpdate={(images) =>
                onUpdate(
                  { ...product, additionalImages: images },
                  isPrintfulProduct,
                )
              }
            />
          </div>
        </div>
      )}
    </div>
  );
};

ProductEditor.propTypes = {
  product: PropTypes.shape({
    _id: PropTypes.string,
    sync_product_id: PropTypes.number,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    price: PropTypes.number,
    retail_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    image: PropTypes.string,
    product: PropTypes.shape({
      image: PropTypes.string,
    }),
    parent_product: PropTypes.shape({
      thumbnail_url: PropTypes.string,
    }),
    files: PropTypes.array,
    category: PropTypes.string,
    inStock: PropTypes.bool,
    hasVariants: PropTypes.bool,
    variants: PropTypes.arrayOf(
      PropTypes.shape({
        size: PropTypes.string.isRequired,
        color: PropTypes.string,
        price: PropTypes.number.isRequired,
        inStock: PropTypes.bool.isRequired,
        isVisible: PropTypes.bool.isRequired,
      }),
    ),
    additionalImages: PropTypes.arrayOf(PropTypes.string),
    sku: PropTypes.string,
    size: PropTypes.string,
    color: PropTypes.string,
  }).isRequired,
  onUpdate: PropTypes.func.isRequired,
  onToggleVariantVisibility: PropTypes.func.isRequired,
  onToggleGroupVisibility: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
};

// Show one thumbnail per variant with a label (size or color)
const VariantMockupDisplay = ({ variants }) => {
  if (!variants || variants.length === 0) return null;

  return (
    <div className="p-4">
      <h3 className="font-bold text-lg mb-4 text-gray-900 dark:text-white">
        Variant Mockups
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {variants.map((v, index) => {
          // Use the correct image path from the database instead of hardcoding .jpg
          // Priority: mockupPaths[0] > product.image > fallback to external_id.jpg
          let imageSrc;
          if (v.mockupPaths && v.mockupPaths.length > 0) {
            imageSrc = v.mockupPaths[0];
            console.log(`Using mockupPaths[0] for ${v.external_id}:`, imageSrc);
          } else if (v.product && v.product.image) {
            imageSrc = v.product.image;
            console.log(`Using product.image for ${v.external_id}:`, imageSrc);
          } else {
            // Fallback to hardcoded path only if no database path exists
            imageSrc = `/images/products/merch/${v.external_id}.jpg`;
            console.log(`Using fallback path for ${v.external_id}:`, imageSrc);
          }
          
          console.log(`Final imageSrc for variant ${v.external_id} (${v.color || 'no-color'} ${v.size || 'no-size'}):`, imageSrc);
          console.log(`Variant data:`, { mockupPaths: v.mockupPaths, product: v.product, external_id: v.external_id });
          
          return (
            <div key={`${v._id}-${index}-${v.external_id || ''}-${v.color || ''}-${v.size || ''}`} className="flex flex-col items-center">
              <ImageWithFallback
                src={imageSrc}
                alt={v.size || v.color || 'variant'}
                className="w-full h-auto rounded border dark:border-gray-700"
              />
              <span className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                {v.size || v.color || ''}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

VariantMockupDisplay.propTypes = {
  variants: PropTypes.arrayOf(
    PropTypes.shape({
      external_id: PropTypes.string,
      size: PropTypes.string,
      color: PropTypes.string,
      _id: PropTypes.string,
    }),
  ),
};

const AdminPage = () => {
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [printfulOrders, setPrintfulOrders] = useState({});
  const [activeTab, setActiveTab] = useState('products');
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [confirmingOrders, setConfirmingOrders] = useState(new Set());
  const { user, loading: authLoading } = useAuth();

  const loadProducts = async () => {
    try {
      const data = await getAllProducts();
      setProducts(data || []);
    } catch (error) {
      toast.error("Failed to load products.");
    }
  };

  const loadOrders = async () => {
    setLoadingOrders(true);
    try {
      const data = await getOrders();
      const ordersWithPrintful = data.filter(order => order.printfulOrderId);
      setOrders(ordersWithPrintful || []);

      // Load Printful order details for orders with Printful IDs
      const printfulData = {};
      for (const order of ordersWithPrintful) {
        if (order.printfulOrderId) {
          try {
            const printfulOrder = await getPrintfulOrder(order.printfulOrderId);
            printfulData[order.printfulOrderId] = printfulOrder;
          } catch (error) {
            console.warn(`Failed to load Printful order ${order.printfulOrderId}:`, error.message);
          }
        }
      }
      setPrintfulOrders(printfulData);
    } catch (error) {
      toast.error("Failed to load orders.");
    } finally {
      setLoadingOrders(false);
    }
  };

  const handleConfirmOrder = async (orderId, printfulOrderId) => {
    setConfirmingOrders(prev => new Set(prev).add(orderId));
    try {
      const confirmedOrder = await confirmPrintfulOrder(printfulOrderId);

      // Update the Printful order data
      setPrintfulOrders(prev => ({
        ...prev,
        [printfulOrderId]: confirmedOrder
      }));

      toast.success('Order confirmed successfully!');
    } catch (error) {
      toast.error(`Failed to confirm order: ${error.message}`);
    } finally {
      setConfirmingOrders(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
    }
  };

  const handleDeleteProduct = async (product) => {
    // Debug logging to understand the product structure
    console.log('Delete product called with:', {
      name: product.name,
      sync_product_id: product.sync_product_id,
      variants: product.variants,
      variantCount: product.variants ? product.variants.length : 0,
      _id: product._id,
      external_id: product.external_id
    });
    
    // Check if this is a Printful product (has sync_product_id)
    // For Printful products, always use group delete to remove all variants
    const hasSyncProductId = product.sync_product_id && product.sync_product_id !== null;
    const variantCount = product.variants ? product.variants.length : 1;
    
    console.log('Group detection:', {
      hasSyncProductId,
      variantCount,
      willUseGroupDelete: hasSyncProductId
    });
    
    // Show confirmation dialog with appropriate messaging
    const confirmed = window.confirm(
      `Are you sure you want to delete "${product.name}"?\n\n` +
      (hasSyncProductId 
        ? `This will permanently remove ALL variants of this Printful product from the database and delete all associated image files. `
        : `This will permanently remove the product from the database and delete all associated image files. `
      ) +
      `This action cannot be undone.\n\n` +
      `Note: This will NOT affect Printful or Stripe - the product can be re-synced from Printful later.`
    );
    
    if (!confirmed) return;

    try {
      let result;
      
      if (hasSyncProductId) {
        console.log('Using group delete for sync_product_id:', product.sync_product_id);
        // Delete entire product group using sync_product_id (removes all variants)
        result = await deleteProductGroup(product.sync_product_id);
        
        // Remove all variants of this product group from local state
        setProducts(prevProducts => 
          prevProducts.filter(p => p.sync_product_id !== product.sync_product_id)
        );
        
        toast.success(
          `Product group "${result.productName}" deleted successfully. ` +
          `${result.deletedVariants} variants and ${result.deletedImages} image files were removed.`
        );
      } else {
        console.log('Using individual delete for product ID:', product._id || product.external_id || product.sync_product_id);
        // Delete individual product
        const productId = product._id || product.external_id || product.sync_product_id;
        result = await deleteProduct(productId);
        
        // Remove the individual product from local state
        setProducts(prevProducts => 
          prevProducts.filter(p => 
            p._id !== product._id && 
            p.external_id !== product.external_id && 
            p.sync_product_id !== product.sync_product_id
          )
        );
        
        toast.success(
          `Product "${result.productName}" deleted successfully. ` +
          `${result.deletedImages} image files were removed.`
        );
      }
      
      if (result.imageErrors && result.imageErrors.length > 0) {
        toast.error(
          `Warning: Failed to delete ${result.imageErrors.length} image files: ${result.imageErrors.join(', ')}`
        );
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error(`Failed to delete product: ${error.message}`);
    }
  };

  useEffect(() => {
    if (user) {
      loadProducts();
      if (activeTab === 'orders') {
        loadOrders();
      }
    }
  }, [user, activeTab]);

  const handleVariantVisibilityToggle = async (variant) => {
    const newVisibility = !variant.isVisible;
    const idToUse = variant._id;
    try {
      const response = await fetch(
        `${API_BASE_URL}/admin/products/${idToUse}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
          body: JSON.stringify({ isVisible: newVisibility }),
        }
      );
      if (!response.ok) throw new Error("Variant visibility update failed");
      await response.json();
      setProducts((prev) =>
        prev.map((p) =>
          p._id === variant._id ? { ...p, isVisible: newVisibility } : p
        )
      );
      toast.success(`Variant visibility updated.`);
    } catch (error) {
      toast.error(`Failed to update variant visibility.`);
    }
  };

  const handleVisibilityToggle = async (product) => {
    const isCurrentlyVisible = product.isVisible !== false;
    const newVisibility = !isCurrentlyVisible;
    const idToUse = product.sync_product_id || product._id || product.id;
    try {
      const response = await fetch(`${API_BASE_URL}/admin/products/${idToUse}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: JSON.stringify({ isVisible: newVisibility }),
      });
      if (!response.ok) {
        throw new Error("Visibility update failed");
      }
      await response.json();

      setProducts((prevProducts) =>
        prevProducts.map((p) =>
          product.sync_product_id
            ? p.sync_product_id === product.sync_product_id
              ? { ...p, isVisible: newVisibility }
              : p
            : p._id === product._id || p.id === product.id
              ? { ...p, isVisible: newVisibility }
              : p
        ),
      );

      toast.success(`${product.name} visibility updated.`);
    } catch (error) {
      toast.error(`Failed to update visibility for ${product.name}.`);
    }
  };

  const handleProductUpdate = async (product, isPrintfulProduct = false) => {
    try {
      let updatedProductData;

      // Use the correct API endpoint based on product type
      if (isPrintfulProduct || product.sync_product_id) {
        // Use the Printful-specific API for Printful products
        updatedProductData = await updatePrintfulProduct(product);
      } else {
        // Use the generic products API for regular products
        const response = await fetch(`${API_BASE_URL}/products/${product._id || product.id}`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
          body: JSON.stringify(product),
        });
        
        if (!response.ok) {
          throw new Error(`Failed to update product (Status: ${response.status})`);
        }
        
        updatedProductData = await response.json();
      }

      setProducts((prevProducts) =>
        prevProducts.map((p) => {
          if (
            p.external_id === product.external_id ||
            p.parent_product?.external_id ===
            product.parent_product?.external_id
          ) {
            return {
              ...p,
              ...product,
              isVisible: product.isVisible !== undefined ? product.isVisible : p.isVisible,
            };
          }
          return p;
        }),
      );

      toast.success(`${product.name} updated successfully.`);
      loadProducts(); // Refresh list
    } catch (error) {
      toast.error(`Failed to update ${product.name}: ${error.message}`);
    }
  };

  const handleSyncPrintful = async () => {
    toast.loading("Syncing Printful products...", { id: "printful-sync" });
    try {
      await syncPrintfulProducts();
      loadProducts(); // Reload products after sync
      toast.success("Printful sync successful!", { id: "printful-sync" });
    } catch (error) {
      toast.error("Printful sync failed.", { id: "printful-sync" });
    }
  };

  const handleSyncStripe = async () => {
    toast.loading("Syncing Stripe products...", { id: "stripe-sync" });
    try {
      await syncStripeProducts();
      toast.success("Stripe sync successful!", { id: "stripe-sync" });
      loadProducts(); // Refresh list after sync
    } catch (error) {
      toast.error("Stripe sync failed.", { id: "stripe-sync" });
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Loading authentication...
      </div>
    );
  }

  if (!user) {
    return <LoginForm />;
  }

  const groupProducts = (productList) => {
    const groups = {
      visibleGroups: {},
      hiddenGroups: {},
    };

    productList.forEach((product) => {
      const character = typeof product.character === 'string'
        ? product.character || 'Uncategorized'
        : product.character?.name || 'Uncategorized';
      
      // Extract base name - if the name contains " / ", take the first part (new Printful format)
      // Otherwise, use the full name (existing products)
      const baseName = product.name && product.name.includes(' / ') 
        ? product.name.split(' / ')[0] 
        : product.name;
      
      const visible = product.isVisible !== false; // treat undefined as true
      const targetGroup = visible ? groups.visibleGroups : groups.hiddenGroups;

      if (!targetGroup[character]) {
        targetGroup[character] = {};
      }
      if (!targetGroup[character][baseName]) {
        targetGroup[character][baseName] = { base: null, variants: [] };
      }

      // Determine if this product is the 'base' (might need refinement based on data structure)
      // For now, assume the first one encountered for a baseName is the base, or one without size?
      if (!targetGroup[character][baseName].base) {
        targetGroup[character][baseName].base = product;
      }
      // Add product as a variant (including the base product itself as a variant)
      targetGroup[character][baseName].variants.push(product);
    });

    return groups;
  };

  const { visibleGroups, hiddenGroups } = groupProducts(products);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
      <Toaster position="top-right" />
      <div className="border-b border-gray-200 dark:border-gray-700 pb-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Admin Dashboard
          </h1>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg mb-6">
          <button
            onClick={() => setActiveTab('products')}
            className={`px-4 py-2 rounded-md font-medium transition-colors ${activeTab === 'products'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
          >
            Product Management
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            className={`px-4 py-2 rounded-md font-medium transition-colors ${activeTab === 'orders'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
          >
            Order Management
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'products' && (
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Products
            </h2>

            <div className="flex space-x-4">
              <Button
                className="flex items-center text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
                onClick={handleSyncPrintful}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Sync Printful
              </Button>
              <Button
                className="flex items-center text-white bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600"
                onClick={handleSyncStripe}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Sync Stripe
              </Button>
            </div>
          </div>

          {/* Visible Products */}
          <div className="mb-12">
            <h1 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
              Visible Products
            </h1>
            {Object.entries(visibleGroups).map(
              ([character, characterProducts]) => (
                <div key={character} className="mb-12">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                    {character}
                  </h2>
                  <div className="space-y-6">
                    {Object.entries(characterProducts).map(
                      ([baseName, productGroup]) => (
                        <div
                          key={`${character}-${baseName}`}
                          className="bg-white dark:bg-gray-800 rounded-lg shadow"
                        >
                          <ProductEditor
                            product={{
                              ...productGroup.base,
                              name: baseName, // Use the clean base name for the product title
                              variants: productGroup.variants.map((variant) => {
                                // Extract color and size from variant name if it follows "Name / Color / Size" format
                                let variantColor = variant.color || '';
                                let variantSize = variant.size || '';
                                
                                if (variant.name && variant.name.includes(' / ')) {
                                  const parts = variant.name.split(' / ');
                                  if (parts.length >= 3) {
                                    variantColor = parts[1] || variantColor;
                                    variantSize = parts[2] || variantSize;
                                  }
                                }
                                
                                return {
                                  size: variantSize,
                                  color: variantColor,
                                  price: parseFloat(variant.price || 0).toFixed(2),
                                  inStock: variant.inStock !== false,
                                  isVisible: variant.isVisible !== false,
                                  sync_product_id: variant.sync_product_id,
                                  external_id: variant.external_id,
                                  _id: variant._id,
                                  // Include mockup paths and product image for VariantMockupDisplay
                                  mockupPaths: variant.mockupPaths,
                                  product: variant.product,
                                };
                              }),
                            }}
                            onUpdate={handleProductUpdate}
                            onToggleVariantVisibility={handleVariantVisibilityToggle}
                            onToggleGroupVisibility={async (groupProduct) => {
                              // Determine current overall visibility
                              const allVisible = groupProduct.variants.every(v => v.isVisible !== false);
                              const newVisibility = !allVisible;

                              // Send patch for each variant in the group
                              await Promise.all(
                                groupProduct.variants.map(async (v) => {
                                  const idToUse = v._id;
                                  await fetch(`${API_BASE_URL}/admin/products/${idToUse}`, {
                                    method: 'PATCH',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      Authorization: `Bearer ${localStorage.getItem('authToken')}`,
                                    },
                                    body: JSON.stringify({ isVisible: newVisibility }),
                                  });
                                }),
                              );
                              loadProducts();
                            }}
                          />
                        </div>
                      ),
                    )}
                  </div>
                </div>
              ),
            )}
          </div>

          {/* Hidden Products */}
          <div className="mb-12 mt-16 border-t pt-8 dark:border-gray-700">
            <h1 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white flex items-center">
              <EyeOff className="h-5 w-5 mr-2" /> Hidden Products
            </h1>
            {Object.entries(hiddenGroups).length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 italic">
                No hidden products
              </p>
            ) : (
              Object.entries(hiddenGroups).map(
                ([character, characterProducts]) => (
                  <div key={character} className="mb-12">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                      {character}
                    </h2>
                    <div className="space-y-6">
                      {Object.entries(characterProducts).map(
                        ([baseName, productGroup]) => (
                          <div
                            key={`${character}-${baseName}-hidden`}
                            className="bg-white dark:bg-gray-800 rounded-lg shadow border-2 border-red-200 dark:border-red-800"
                          >
                            <ProductEditor
                              product={{
                                ...productGroup.base,
                                name: baseName, // Use the clean base name for the product title
                                variants: productGroup.variants.map(
                                  (variant) => {
                                    // Extract color and size from variant name if it follows "Name / Color / Size" format
                                    let variantColor = variant.color || '';
                                    let variantSize = variant.size || '';
                                    
                                    if (variant.name && variant.name.includes(' / ')) {
                                      const parts = variant.name.split(' / ');
                                      if (parts.length >= 3) {
                                        variantColor = parts[1] || variantColor;
                                        variantSize = parts[2] || variantSize;
                                      }
                                    }
                                    
                                    return {
                                      size: variantSize,
                                      color: variantColor,
                                      price: parseFloat(variant.price || 0).toFixed(2),
                                      inStock: variant.inStock !== false,
                                      isVisible: variant.isVisible !== false,
                                      sync_product_id: variant.sync_product_id,
                                      external_id: variant.external_id,
                                      _id: variant._id,
                                      mockupPaths: variant.mockupPaths,
                                      product: variant.product,
                                    };
                                  },
                                ),
                              }}
                              onUpdate={handleProductUpdate}
                              onToggleVariantVisibility={handleVariantVisibilityToggle}
                              onToggleGroupVisibility={async (groupProduct) => {
                                const allVisible = groupProduct.variants.every(v => v.isVisible !== false);
                                const newVisibility = !allVisible;
                                await Promise.all(groupProduct.variants.map(async (v) => {
                                  await fetch(`${API_BASE_URL}/admin/products/${v._id}`, {
                                    method: 'PATCH',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      Authorization: `Bearer ${localStorage.getItem('authToken')}`,
                                    },
                                    body: JSON.stringify({ isVisible: newVisibility }),
                                  });
                                }));
                                loadProducts();
                              }}
                              onDelete={handleDeleteProduct}
                            />
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                ),
              )
            )}
          </div>
        </div>
      )}

      {/* Order Management Tab */}
      {activeTab === 'orders' && (
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Printful Order Management
            </h2>
            <Button
              onClick={loadOrders}
              disabled={loadingOrders}
              className="flex items-center text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loadingOrders ? 'animate-spin' : ''}`} />
              {loadingOrders ? 'Loading...' : 'Refresh Orders'}
            </Button>
          </div>

          {loadingOrders ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-gray-500 dark:text-gray-400">Loading orders...</div>
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-500 dark:text-gray-400 mb-4">
                No orders with Printful integration found.
              </div>
              <p className="text-sm text-gray-400 dark:text-gray-500">
                Orders will appear here after customers complete checkout with Printful products.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map((order) => {
                const printfulOrder = printfulOrders[order.printfulOrderId];
                const isConfirming = confirmingOrders.has(order._id);
                const isDraft = printfulOrder?.status === 'draft';

                return (
                  <div
                    key={order._id}
                    className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border dark:border-gray-700"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                          Order #{order._id.slice(-8)}
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-gray-600 dark:text-gray-400">
                              <span className="font-medium">Customer:</span> {order.shippingAddress?.name || 'N/A'}
                            </p>
                            <p className="text-gray-600 dark:text-gray-400">
                              <span className="font-medium">Total:</span> ${order.totalPrice?.toFixed(2) || '0.00'}
                            </p>
                            <p className="text-gray-600 dark:text-gray-400">
                              <span className="font-medium">Order Date:</span> {new Date(order.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-600 dark:text-gray-400">
                              <span className="font-medium">Printful ID:</span> {order.printfulOrderId}
                            </p>
                            <p className="text-gray-600 dark:text-gray-400">
                              <span className="font-medium">Status:</span>{' '}
                              <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${printfulOrder?.status === 'draft'
                                  ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                  : printfulOrder?.status === 'pending'
                                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                    : printfulOrder?.status === 'fulfilled'
                                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                      : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                }`}>
                                {printfulOrder?.status || 'Unknown'}
                              </span>
                            </p>
                            {printfulOrder?.tracking_number && (
                              <p className="text-gray-600 dark:text-gray-400">
                                <span className="font-medium">Tracking:</span>{' '}
                                <a
                                  href={printfulOrder.tracking_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 dark:text-blue-400 hover:underline"
                                >
                                  {printfulOrder.tracking_number}
                                </a>
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-2 ml-4">
                        {isDraft && (
                          <Button
                            onClick={() => handleConfirmOrder(order._id, order.printfulOrderId)}
                            disabled={isConfirming}
                            className="bg-green-600 hover:bg-green-700 text-white"
                            size="sm"
                          >
                            {isConfirming ? (
                              <>
                                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                Confirming...
                              </>
                            ) : (
                              'Confirm Order'
                            )}
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Order Items */}
                    <div className="border-t dark:border-gray-700 pt-4">
                      <h4 className="font-medium text-gray-900 dark:text-white mb-3">Order Items</h4>
                      <div className="space-y-2">
                        {order.orderItems?.map((item, index) => (
                          <div key={index} className="flex items-center justify-between text-sm">
                            <div className="flex items-center">
                              {item.image && (
                                <img
                                  src={item.image}
                                  alt={item.name}
                                  className="w-10 h-10 object-cover rounded mr-3"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              )}
                              <div>
                                <p className="text-gray-900 dark:text-white font-medium">{item.name}</p>
                                <p className="text-gray-600 dark:text-gray-400">Qty: {item.quantity}</p>
                              </div>
                            </div>
                            <p className="text-gray-900 dark:text-white font-medium">
                              ${(item.price * item.quantity).toFixed(2)}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Shipping Address */}
                    {order.shippingAddress && (
                      <div className="border-t dark:border-gray-700 pt-4 mt-4">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-2">Shipping Address</h4>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          <p>{order.shippingAddress.street}</p>
                          {order.shippingAddress.street2 && (
                            <p>{order.shippingAddress.street2}</p>
                          )}
                          <p>
                            {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zipCode}
                          </p>
                          <p>{order.shippingAddress.country}</p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminPage;
